import React from "react";
import { AddressDeliveryChecker } from "@/components/public/AddressDeliveryChecker";
import { ChevronDown, Shield, Clock, Star } from "lucide-react";

interface HeroSectionProps {
  id?: string;
}

export function HeroSection2({ id }: HeroSectionProps) {
  return (
    <section
      id={id}
      className="relative w-full h-[105vh] bg-[var(--homepage-bg)]"
      style={{ overflow: 'visible' }}
    >
      {/* Fullscreen Video - Base layer */}
      <video
        className="absolute inset-0 w-full h-full object-cover z-0"
        src="/assets/videos/header-hero-video-2.mp4"
        autoPlay
        loop
        muted
        playsInline
      />
      
      {/* Subtle dark overlay on video for text contrast */}
      <div
        className="pointer-events-none absolute inset-0 z-[1]"
        style={{
          background: 'linear-gradient(180deg, rgba(10,20,30,0.5) 0%, rgba(10,20,30,0.35) 40%, rgba(10,20,30,0.4) 70%, rgba(10,20,30,0.55) 100%)'
        }}
      />

      {/* Top Fade - Darker but short, quick fade to nothing */}
      <div
        className="pointer-events-none absolute top-0 left-0 w-full h-16 z-[3]"
        style={{
          background: 'linear-gradient(to bottom, rgba(2,8,16,0.7) 0%, rgba(2,8,16,0.3) 50%, transparent 100%)'
        }}
      />

      {/* Centered Headline and CTA */}
      <div className="absolute inset-0 flex flex-col items-center justify-center z-30 px-4">
        <div className="relative max-w-4xl mx-auto w-full text-center">

          {/* Eyebrow badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-6 rounded-full border border-white/15 bg-white/5 backdrop-blur-sm">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-xs sm:text-sm text-white font-medium tracking-wide uppercase">
              Now serving UK &amp; Ireland
            </span>
          </div>

          {/* Main headline */}
          <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl text-white leading-[1.1] tracking-tight">
            <span className="font-light">Turnover and replenishment,</span>
            <br />
            <span
              className="font-black text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl bg-clip-text text-transparent"
              style={{
                backgroundImage: 'linear-gradient(135deg, #ffffff 0%, var(--homepage-accent) 100%)'
              }}
            >
              orchestrated
            </span>
          </h1>

          {/* Sub-headline */}
          <p className="mt-5 sm:mt-6 text-base sm:text-lg md:text-xl text-white/85 max-w-2xl mx-auto font-light leading-relaxed">
            Real-time control, predictable costs, and standards you can verify across every unit
          </p>

          {/* Address Delivery Checker */}
          <div className="mt-8 sm:mt-10">
            <AddressDeliveryChecker />
          </div>

          {/* Trust signals */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            <div className="flex items-center gap-1.5">
              <Shield className="w-3.5 h-3.5 text-white" />
              <span className="text-xs text-white font-medium">Quality assured</span>
            </div>
            <div className="hidden sm:block w-px h-3 bg-white/25" />
            <div className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-white" />
              <span className="text-xs text-white font-medium">Same-day delivery</span>
            </div>
            <div className="hidden sm:block w-px h-3 bg-white/25" />
            <div className="flex items-center gap-1.5">
              <Star className="w-3.5 h-3.5 text-white" />
              <span className="text-xs text-white font-medium">Trusted by 5,000+ hosts</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Bottom Divider */}
      <div className="absolute bottom-0 left-0 right-0 z-10 pb-4">        
        <div className="absolute left-1/2 top-0 transform -translate-x-1/2 -translate-y-1/2 z-20 mt-4">
          <div className="px-4 py-2 bg-gray-50/90 backdrop-blur-sm border border-gray-200/50 rounded-full whitespace-nowrap">
            <div className="flex items-center gap-2">
              <span className="text-gray-700 text-sm font-medium tracking-wider">
                <span className="hidden lg:inline">WHAT REPLENICO IS</span>
                <span className="lg:hidden">REPLENICO</span>
              </span>
              <ChevronDown className="w-4 h-4 text-gray-700" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}